// ./src/components/features/Artists/EventArtistBookingDrawer/performanceRules.ts
//
// #165 round 2 — an act's SET on each date it plays, as the booking drawer edits it: a wall-clock time on that date in the
// EVENT's zone ("9:30 PM"), stored as an instant. Pure, so check-artist-dates (apps/app) runs it.
//
// ⚠ A set after midnight belongs to the date it is part of: a time EARLIER than the date's own start (a 9 PM – 2 AM night,
//   a set at 12:30 AM) is the NEXT calendar day; an end earlier than its start, likewise. The day is stepped as a DATE
//   ("2026-10-18" → "2026-10-19"), never as 24 hours — on a daylight-saving night those differ by an hour.
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

dayjs.extend(utc);
dayjs.extend(timezone);

/** One date's row in the drawer: the date, the set's local times ("" = the date's own), the stage ("" = the date's). */
export interface PerformanceRow {
  eventTimeSlotId: string;
  start: string;
  end: string;
  mapElementId: string;
}

const zoneOf = (tz: string | null | undefined): string => tz || dayjs.tz.guess();

/** "21:30" for an instant, in the event's zone ("" for none). */
export const localTimeOf = (iso: string | null | undefined, tz: string | null | undefined): string =>
  iso ? dayjs.utc(iso).tz(zoneOf(tz)).format('HH:mm') : '';

/** "2026-10-18" — the calendar date of an instant, in the event's zone. */
export const localDateOf = (iso: string | null | undefined, tz: string | null | undefined): string =>
  iso ? dayjs.utc(iso).tz(zoneOf(tz)).format('YYYY-MM-DD') : '';

const nextDate = (date: string): string => dayjs.utc(`${date}T00:00:00Z`).add(1, 'day').format('YYYY-MM-DD');

const instant = (date: string, time: string, tz: string | null | undefined): string =>
  dayjs.tz(`${date}T${time}:00`, zoneOf(tz)).utc().toISOString();

/**
 * The instants to save for a row on its date. `slotStartsAt` is the date's own start. "" → null (the date's own time). A
 * start before the date's local start time is the next day; an end before the start, the day after the start's.
 */
export const setInstants = (
  row: Pick<PerformanceRow, 'start' | 'end'>,
  slotStartsAt: string | null | undefined,
  tz: string | null | undefined,
): { setStartsAt: string | null; setEndsAt: string | null } => {
  if (!slotStartsAt || !row.start) {
    return { setStartsAt: null, setEndsAt: null };
  }

  const day = localDateOf(slotStartsAt, tz);
  const startDay = row.start < localTimeOf(slotStartsAt, tz) ? nextDate(day) : day;
  const setStartsAt = instant(startDay, row.start, tz);
  const setEndsAt = row.end ? instant(row.end < row.start ? nextDate(startDay) : startDay, row.end, tz) : null;

  return { setStartsAt, setEndsAt };
};

/** The drawer's rows for a booking's saved performances. */
export const performanceRowsOf = (
  performances: Array<{ eventTimeSlotId: string; setStartsAt?: string | null; setEndsAt?: string | null; mapElementId?: string | null }> | null | undefined,
  tz: string | null | undefined,
): PerformanceRow[] =>
  (performances ?? []).map((p) => ({
    eventTimeSlotId: p.eventTimeSlotId,
    start: localTimeOf(p.setStartsAt, tz),
    end: p.setStartsAt ? localTimeOf(p.setEndsAt, tz) : '',
    mapElementId: p.mapElementId ?? '',
  }));
